import { getArticlesFromCart } from "@/graphql/schemas/shared/utils/getArticles"
import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getActiveCustomerCart, getCartDiscountOption } from "../utils/getCart"

/**
 * Differs from query customer carts
 *
 * Retrieves only an active cart as an object provided customer id
 * query customer carts used to retrieve previous orders if there are any
 */
export const queryCustomerCart = {
  async customerCart(parent, args, context, info) {
    const { customerId, customerNumber, lang, cdn, loggedInBranch, regionSanityId, enriched, checkout } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.READ,
    })

    const customer = await getCustomer({ number: customerNumber, id: customerId })

    if (!customer) return null

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    const cart = await getActiveCustomerCart(customer.id, user.employeeNumber)

    if (!cart[0]) {
      return null
    }

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    let reservationNumber = ""
    let picklistNumber = ""
    if (checkout) {
      if (cart[0]?.custom?.fields?.reservationNumber) {
        reservationNumber = await getReservationNumber(cart[0]?.custom?.fields?.reservationNumber)
      }
      if (cart[0]?.custom?.fields?.picklistNumber) {
        picklistNumber = await getReservationNumber(cart[0]?.custom?.fields?.picklistNumber)
      }
    }

    if (!enriched) {
      return { ...cart[0], reservationNumber, picklistNumber }
    }

    const updatedCart = await getArticlesFromCart(
      cart[0],
      client,
      loggedInBranch,
      enriched,
      user,
      customer.customerNumber,
      regionSanityId,
    )

    if (!updatedCart) return null

    const freightSurchargeValue = updatedCart?.custom?.fields?.freightSurcharge?.centAmount || 0
    const totalGrossValue = (updatedCart?.taxedPrice?.totalGross?.centAmount || 0) + freightSurchargeValue

    const discountValue = getCartDiscountOption(
      customer?.custom?.fields?.discountKey?.id,
      customer?.custom?.fields?.discountKey?.obj,
      totalGrossValue,
    )

    if (discountValue === null) {
      return {
        ...updatedCart,
        sokoDiscountValue: null,
        sokoDiscountPercent: null,
        sokoPrice: null,
        reservationNumber,
        picklistNumber,
      }
    }

    const { discount, percent, sokoTotal } = discountValue

    return {
      ...updatedCart,
      sokoDiscountValue: discount,
      sokoDiscountPercent: percent,
      sokoPrice: sokoTotal,
      reservationNumber,
      picklistNumber,
    }
  },
}

const getReservationNumber = async (orderNumber) => {
  try {
    const graphiQLQueryObject = [
      {
        method: "findOrder",
        variables: { orderNumber },
        fields: ["orderType", "orderNumber2"],
        expandedFields: {
          items: ["amount", "posId"],
        },
      },
    ]

    const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
    const reservationResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.findOrder

    if (!reservationResponse) return ""

    return `${reservationResponse?.orderType}-${reservationResponse?.orderNumber2}`
  } catch (e) {
    console.error(e)
    return ""
  }
}
